/**
 * CR098: Header Composition Panel
 * 
 * Controls how promo merchant vs reward brand is displayed on deal cards.
 * Stored in deal_card_templates.template_json.header
 */

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Info, LayoutTemplate } from "lucide-react";

// ================================================================
// TYPES
// ================================================================

export type HeaderFormat = 
  | "PROMO_ONLY" 
  | "PROMO_THEN_REWARD" 
  | "REWARD_ONLY" 
  | "REWARD_THEN_PROMO";

export interface HeaderComposition {
  show_promo_merchant: boolean;
  show_reward_brand: boolean;
  show_reward_type_chip: boolean;
  promo_merchant_label: string;
  reward_brand_label: string;
  format: HeaderFormat;
}

export const DEFAULT_HEADER_COMPOSITION: HeaderComposition = {
  show_promo_merchant: true,
  show_reward_brand: true,
  show_reward_type_chip: true,
  promo_merchant_label: "Spend at",
  reward_brand_label: "Reward",
  format: "PROMO_THEN_REWARD",
};

interface HeaderCompositionPanelProps {
  header: HeaderComposition;
  onChange: (update: Partial<HeaderComposition>) => void;
}

// ================================================================
// FORMAT OPTIONS
// ================================================================

const FORMAT_OPTIONS: { value: HeaderFormat; label: string; description: string }[] = [
  { 
    value: "PROMO_ONLY", 
    label: "Promo merchant only", 
    description: "Show only where to buy" 
  },
  { 
    value: "PROMO_THEN_REWARD", 
    label: "Promo → Reward (recommended)", 
    description: "Show purchase location, then reward brand" 
  },
  { 
    value: "REWARD_ONLY", 
    label: "Reward only", 
    description: "Show only the reward brand" 
  },
  { 
    value: "REWARD_THEN_PROMO", 
    label: "Reward → Promo", 
    description: "Lead with reward brand" 
  },
];

// ================================================================
// COMPONENT
// ================================================================

export function HeaderCompositionPanel({
  header,
  onChange,
}: HeaderCompositionPanelProps) {
  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
          <Label className="font-medium">Header Composition</Label>
        </div>
        <Badge variant="outline" className="text-xs">
          <Info className="h-3 w-3 mr-1" />
          Stored in Template
        </Badge>
      </div>

      <p className="text-xs text-muted-foreground">
        Controls how "Buy $99 Nintendo, get $20 Walmart" deals display promo merchant vs reward brand.
      </p>

      <div className="grid grid-cols-1 gap-4">
        {/* Format Dropdown */}
        <div className="space-y-2">
          <Label className="text-sm">Display Format</Label>
          <Select
            value={header.format}
            onValueChange={(v: HeaderFormat) => onChange({ format: v })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FORMAT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  <div className="flex flex-col items-start">
                    <span>{opt.label}</span>
                    <span className="text-xs text-muted-foreground">{opt.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Toggles Row */}
        <div className="grid grid-cols-1 gap-3 pt-2 border-t">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm">Show "Spend at" line</Label>
              <p className="text-xs text-muted-foreground">Promo merchant chip/line</p>
            </div>
            <Switch
              checked={header.show_promo_merchant}
              onCheckedChange={(v) => onChange({ show_promo_merchant: v })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm">Show reward brand</Label>
              <p className="text-xs text-muted-foreground">Reward brand chip/line</p>
            </div>
            <Switch
              checked={header.show_reward_brand}
              onCheckedChange={(v) => onChange({ show_reward_brand: v })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm">Show reward type chip</Label>
              <p className="text-xs text-muted-foreground">e.g., "Gift Card", "Cash Back"</p>
            </div>
            <Switch
              checked={header.show_reward_type_chip}
              onCheckedChange={(v) => onChange({ show_reward_type_chip: v })}
            />
          </div>
        </div>

        {/* Label Customization */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Promo Label</Label>
            <Input
              value={header.promo_merchant_label}
              onChange={(e) => onChange({ promo_merchant_label: e.target.value })}
              placeholder="Spend at"
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Reward Label</Label>
            <Input
              value={header.reward_brand_label}
              onChange={(e) => onChange({ reward_brand_label: e.target.value })}
              placeholder="Reward"
              className="h-8 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Example Preview */}
      <div className="pt-3 border-t">
        <Label className="text-xs text-muted-foreground mb-2 block">Example (Nintendo → Walmart deal)</Label>
        <div className="text-sm p-2 bg-background rounded border">
          {header.format === "PROMO_ONLY" && header.show_promo_merchant && (
            <span className="text-muted-foreground">{header.promo_merchant_label}: </span>
          )}
          {header.format === "PROMO_ONLY" && <span className="font-medium">Nintendo</span>}
          
          {header.format === "PROMO_THEN_REWARD" && (
            <>
              {header.show_promo_merchant && (
                <>
                  <span className="text-muted-foreground">{header.promo_merchant_label}: </span>
                  <span className="font-medium">Nintendo</span>
                </>
              )}
              {header.show_promo_merchant && header.show_reward_brand && " → "}
              {header.show_reward_brand && (
                <>
                  <span className="text-muted-foreground">{header.reward_brand_label}: </span>
                  <span className="font-medium">Walmart</span>
                </>
              )}
            </>
          )}
          
          {header.format === "REWARD_ONLY" && header.show_reward_brand && (
            <>
              <span className="text-muted-foreground">{header.reward_brand_label}: </span>
              <span className="font-medium">Walmart</span>
            </>
          )}
          
          {header.format === "REWARD_THEN_PROMO" && (
            <>
              {header.show_reward_brand && (
                <>
                  <span className="text-muted-foreground">{header.reward_brand_label}: </span>
                  <span className="font-medium">Walmart</span>
                </>
              )}
              {header.show_reward_brand && header.show_promo_merchant && " ← "}
              {header.show_promo_merchant && (
                <>
                  <span className="text-muted-foreground">{header.promo_merchant_label}: </span>
                  <span className="font-medium">Nintendo</span>
                </>
              )}
            </>
          )}
          
          {header.show_reward_type_chip && (
            <Badge variant="secondary" className="ml-2 text-xs">Gift Card</Badge>
          )}
        </div>
      </div>
    </div>
  );
}
